import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Close } from '@zocdoc-frontend-common/assets-icons';
import {
    injectKeyHandler,
    Keys,
} from '@zocdoc-frontend-common/components-keyhandler';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { columnSize, ColumnAndRow } from '@zocdoc-frontend-common/styles-grid';
import shouldUsePortal from '@zocdoc-frontend-common/utils-deviceutils/shouldUsePortal';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { ScrollLock } from '@zocdoc-frontend-common/utils-scrolllock';
import useModalManager from './useModalManager';

/**
 * @param {string} desktopBreakpoint
 * @param {boolean} disableUnnecessaryRemount
 * @param {boolean} ignoreClickOutside
 * @param {function(): void} onClickCancel
 * @param {boolean} useScrollLock
 * @returns {JSX.Element}
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-modal'
 */
class ModalView extends PureComponent {
    static defaultProps = {
        disableUnnecessaryRemount: false, // default is false for backwards compatibility purposes
        ignoreClickOutside: false,
        useScrollLock: true,
        desktopBreakpoint: 'small',
    };

    getOrCreateElementForPortal = () => {
        if (!this.elementForPortal) {
            this.elementForPortal = document.createElement('div');
            this.elementForPortal.id = 'modal-root';
        }

        return this.elementForPortal;
    };

    componentDidMount() {
        if (shouldUsePortal() && !this.elementForPortal) {
            const el = this.getOrCreateElementForPortal();
            this.mainEl =
                document.getElementById('root') ||
                document.getElementById('main') ||
                document.body;
            this.mainEl.appendChild(el);
            this.forceUpdate();
        }
    }

    componentWillUnmount() {
        if (shouldUsePortal() && this.elementForPortal) {
            this.mainEl.removeChild(this.elementForPortal);
        }
    }

    render() {
        const { contentZIndex, hideOverlay, overlayZIndex, desktopBreakpoint } =
            this.props;
        if (!this.elementForPortal && this.props.disableUnnecessaryRemount)
            return null;

        const MainElement = this.elementForPortal ? Portal : 'div';
        const props = this.elementForPortal
            ? { rootElement: this.elementForPortal }
            : {};

        return (
            <MainElement {...props}>
                <div {...filterToValidAttributes(this.props)}>
                    <ModalOverlay
                        data-test="modal-body-overlay"
                        hidden={hideOverlay}
                        zIndex={overlayZIndex}
                    />

                    <StyledContainer data-test="modal-container">
                        <StyledClickLayer
                            data-test="modal-fixed"
                            onClick={this.handleClickOutside}
                            zIndex={contentZIndex}
                        >
                            <ModalContent
                                data-test="modal-content"
                                zIndex={contentZIndex}
                                desktopBreakpoint={desktopBreakpoint}
                            >
                                {this.maybeRenderCloseIcon()}
                                {this.props.children}
                            </ModalContent>
                        </StyledClickLayer>
                    </StyledContainer>
                </div>
                {this.props.useScrollLock && <ScrollLock />}
            </MainElement>
        );
    }

    maybeRenderCloseIcon() {
        if (this.props.onClickCancel) {
            return (
                <StyledClose
                    data-test="close-icon"
                    height={16}
                    onClick={this.props.onClickCancel}
                />
            );
        }
    }

    handleClickOutside = (e) => {
        const { ignoreClickOutside, onClickCancel } = this.props;

        if (
            !ignoreClickOutside &&
            onClickCancel &&
            e.target === e.currentTarget
        ) {
            onClickCancel();
        }
    };
}

const Portal = ({ children, rootElement }) =>
    ReactDOM.createPortal(
        <div>{children}</div>, // enzyme tests were only passing with this added div
        rootElement
    );

const ModalWithCloseOnEscape = injectKeyHandler({
    [Keys.ESCAPE]: (props) => {
        if (props.onClickCancel) {
            props.onClickCancel();
        }
    },
})(ModalView);

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-modal'
 */
// Wrapper that supports enforcing only one modal at a time
const Modal = ({ isManaged = false, ...props }) => {
    const { contentZIndex, hideOverlay, overlayZIndex } =
        useModalManager(isManaged);
    return (
        <ModalWithCloseOnEscape
            {...props}
            contentZIndex={contentZIndex}
            hideOverlay={hideOverlay}
            overlayZIndex={overlayZIndex}
        />
    );
};

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-modal'
 */
const ModalContent = styled.div`
    ${columnSize(24)}
    background-color: #FFF;
    bottom: 0;
    box-sizing: border-box;
    float: left;
    left: 0;
    max-height: 75vh;
    overflow: auto;
    padding: 40px 16px 18px;
    position: fixed;
    z-index: ${(p) => p.zIndex || 12};

    ${(p) => breakpoint[p.desktopBreakpoint]} {
        ${columnSize(22)}
        box-sizing: border-box;
        height: auto;
        min-width: 586px;
        max-width: 800px;
        padding: 44px 70px;
        position: relative;
    }
`;

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-modal'
 */
const ModalOverlay = styled.div`
    background-color: #000;
    opacity: 0.6;
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    visibility: ${(p) => (p.hidden ? 'hidden' : 'visible')};
    z-index: ${(p) => p.zIndex || 11};
`;

const StyledClickLayer = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: ${(p) => p.zIndex || 12};
`;

const StyledContainer = styled(ColumnAndRow)`
    box-sizing: border-box;
    position: relative;
`;

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-modal'
 */
const StyledClose = styled(Close)`
    cursor: pointer;
    position: absolute;
    right: 16px;
    top: 16px;
`;

export {
    Modal as default,
    Modal,
    ModalView,
    ModalContent,
    ModalOverlay,
    StyledClose,
};
